import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button } from 'components/Button';
import { RETURN_LABEL_PURCHASE_PREVIEW } from 'gql/queries';

interface PurchasePreview {
  returnId: string;
  storeId: string;
  returnUpdatedAt: string;
  purchaseRequired: boolean;
  canPurchase: boolean;
  blockedReason?: string | null;
  quotedAt: string;
  carrier?: string | null;
  service?: string | null;
  carrierAmount?: number | null;
  carrierCurrency?: string | null;
  shipaidFeeAmount?: number | null;
  shipaidFeeCurrency?: string | null;
  estimatedTotal?: number | null;
  totalCurrency?: string | null;
}

const amountIsValid = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;
const currencyIsValid = (value?: string | null): value is string =>
  Boolean(value && /^[A-Z]{3}$/.test(value));
const quoteIsFresh = (quotedAt?: string) => {
  const age = Date.now() - Date.parse(quotedAt || '');
  return Number.isFinite(age) && age >= -60000 && age < 120000;
};
const money = (amount: number, currency: string) =>
  `${new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
    amount,
  )} ${currency}`;

export function ReturnApprovalConfirmation({
  returnId,
  storeId,
  pending,
  onCancel,
  onConfirm,
}: {
  returnId: string;
  storeId: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (expectedUpdatedAt: string) => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [expired, setExpired] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const { data, loading, error, refetch } = useQuery<{
    returnLabelPurchasePreview: PurchasePreview;
  }>(RETURN_LABEL_PURCHASE_PREVIEW, {
    variables: { input: { returnId, storeId } },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const quote = data?.returnLabelPurchasePreview;
  const current = quote?.returnId === returnId && quote?.storeId === storeId;
  const validAmounts = Boolean(
    quote &&
      (quote.purchaseRequired === false ||
        (amountIsValid(quote.carrierAmount) &&
          currencyIsValid(quote.carrierCurrency) &&
          amountIsValid(quote.shipaidFeeAmount) &&
          currencyIsValid(quote.shipaidFeeCurrency))),
  );
  const fresh = !expired && quoteIsFresh(quote?.quotedAt);
  const busy = loading || refreshing || pending;
  const ready = Boolean(
    !busy &&
      !error &&
      !refreshFailed &&
      current &&
      fresh &&
      validAmounts &&
      quote?.returnUpdatedAt &&
      (quote.purchaseRequired === false || quote.canPurchase === true),
  );
  const refresh = async () => {
    setRefreshing(true);
    setRefreshFailed(false);
    try {
      await refetch();
      setExpired(false);
    } catch {
      setRefreshFailed(true);
    } finally {
      setRefreshing(false);
    }
  };
  const confirm = () => {
    if (!quoteIsFresh(quote?.quotedAt)) {
      setExpired(true);
      return;
    }
    if (ready && quote) onConfirm(quote.returnUpdatedAt);
  };

  return (
    <section aria-label="Review return approval" className="max-w-lg space-y-4">
      <h2 className="text-lg font-semibold">Review return approval</h2>
      {loading || refreshing ? (
        <p role="status">Checking the current label price…</p>
      ) : (
        <>
          {current && quote?.purchaseRequired && validAmounts && (
            <>
              <p className="text-sm">
                Approving this return purchases a{' '}
                {[quote.carrier, quote.service].filter(Boolean).join(' ')}{' '}
                label.
              </p>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-6">
                  <dt>Estimated carrier postage</dt>
                  <dd>{money(quote.carrierAmount!, quote.carrierCurrency!)}</dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt>ShipAid label fee</dt>
                  <dd>
                    {money(quote.shipaidFeeAmount!, quote.shipaidFeeCurrency!)}
                  </dd>
                </div>
                {amountIsValid(quote.estimatedTotal) &&
                  currencyIsValid(quote.totalCurrency) && (
                    <div className="flex justify-between gap-6 font-semibold">
                      <dt>Total estimated merchant cost</dt>
                      <dd>
                        {money(quote.estimatedTotal, quote.totalCurrency)}
                      </dd>
                    </div>
                  )}
              </dl>
              <p className="text-sm text-gray-600">
                The carrier price may change before purchase. These merchant
                charges are separate from the customer&apos;s return-fee deduction.
              </p>
            </>
          )}
          {current && quote?.purchaseRequired === false && (
            <p className="text-sm">
              Approval will not purchase a label for this request.
            </p>
          )}
          {!ready && !pending && (
            <p role="alert" className="text-sm text-red-700">
              {error || refreshFailed || !current
                ? 'Unable to verify the label price. Refresh the quote before approving.'
                : !fresh
                ? 'This quote has expired. Refresh it before approving.'
                : quote?.blockedReason ||
                  (!validAmounts
                    ? 'Unable to verify the label price. Refresh the quote before approving.'
                    : 'This return cannot be approved with a label purchase right now.')}
            </p>
          )}
          <p className="text-sm text-gray-600">
            Depending on the return policy, approval may also issue a refund or
            store credit.
          </p>
        </>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outlined" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button variant="outlined" onClick={refresh} disabled={busy}>
          Refresh quote
        </Button>
        <Button onClick={confirm} disabled={!ready} isLoading={pending}>
          {quote?.purchaseRequired === false
            ? 'Confirm approval'
            : 'Confirm approval and purchase'}
        </Button>
      </div>
    </section>
  );
}
